import { CompositeDecorator, Editor, EditorState, RichUtils } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  AiOutlineLink,
  AiOutlineOrderedList,
  AiOutlineUnorderedList,
} from 'react-icons/ai';
import { BsBlockquoteLeft } from 'react-icons/bs';
import { FiBold, FiItalic, FiUnderline } from 'react-icons/fi';
import { Tooltip } from 'react-tooltip';
import RedactorLink, { RedactorLinkModal } from './redactor-link-component';
import Styled from './redactor-style';
import { applyDecorators, findLinkEntities, myKeyBindingFn } from './util';

const styledTooltip = {
  backgroundColor: 'white',
  color: '#000',
  fontWeight: '700',
  border: '1px solid #DFDFDF',
  boxShadow: '0px 3px 5px 0px rgba(130,130,130,0.75)',
};

const INLINE_STYLES = [
  { icon: <FiBold />, style: 'BOLD' },
  { icon: <FiItalic />, style: 'ITALIC' },
  { icon: <FiUnderline />, style: 'UNDERLINE' },
];

const BLOCK_TYPES = [
  { icon: <AiOutlineOrderedList />, style: 'ordered-list-item' },
  { icon: <AiOutlineUnorderedList />, style: 'unordered-list-item' },
  { icon: <BsBlockquoteLeft />, style: 'blockquote' },
  { icon: <AiOutlineLink />, style: 'link' },
];

const InlineStyleControls = ({ editorState, onToggle }) => {
  const currentStyle = editorState.getCurrentInlineStyle();
  return (
    <>
      {INLINE_STYLES.map((type, i) => (
        <StyleButton
          key={type.style}
          active={currentStyle.has(type.style)}
          icon={type.icon}
          onToggle={onToggle}
          style={type.style}
          index={i}
        />
      ))}
    </>
  );
};

InlineStyleControls.propTypes = {
  editorState: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const BlockStyleControls = ({ editorState, onToggle }) => {
  const selection = editorState.getSelection();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();
  return (
    <>
      {BLOCK_TYPES.map((type, i) => (
        <StyleButton
          key={type.style}
          active={type.style === blockType}
          icon={type.icon}
          onToggle={onToggle}
          style={type.style}
          index={i}
        />
      ))}
    </>
  );
};

BlockStyleControls.propTypes = {
  editorState: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const StyleButton = ({ style, active, onToggle, icon, index }) => {
  let className = 'material-icons';
  if (active) {
    className += ' active';
  }

  return (
    <i
      className={className}
      role="menuitem"
      title="Toggle to change style"
      tabIndex={index}
      onMouseDown={() => {
        onToggle(style);
      }}
    >
      {icon}
    </i>
  );
};

StyleButton.propTypes = {
  style: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  icon: PropTypes.element.isRequired,
  index: PropTypes.number.isRequired,
  onToggle: PropTypes.func.isRequired,
};

/**
 * @param {object} root0 - The root object containing the component props
 * @param {EditorState} root0.value - The current value of the editor state
 * @param {Function} root0.onChange - The function to call when the editor state changes
 * @param {string} root0.className - The CSS class name to apply to the editor
 * @param {boolean} root0.header - Whether to show the header or not
 * @param {boolean} root0.fullScreen - Whether to show the editor in full screen mode or not
 * @param {string} root0.placeholder - The placeholder text to show when the editor is empty
 * @param {boolean} root0.border - Whether to show a border around the editor or not
 * @param {boolean} root0.disabled - Whether the editor should be disabled or not
 * @param {string} root0.cy - The Cypress selector for testing purposes
 * @param {boolean} root0.newVersion - Whether the editor is in new version or not.
 * @param {Function} root0.onBlur - The function to call when the editor loses focus.
 * @returns {JSX.Element} - The rendered Redactor component
 */
const Redactor = ({
  value: editorState,
  onChange,
  className,
  header,
  fullScreen,
  placeholder,
  border,
  disabled,
  cy,
  newVersion,
  onBlur,
}) => {
  const { setValue } = useFormContext();
  const editor = useRef(null);
  const [linkUrl, setLinkUrl] = useState('https://');

  const [isRedactorLinkOpen, setIsRedactorLinkOpen] = useState(false);
  const compositeDecorators = new CompositeDecorator([
    {
      strategy: findLinkEntities,
      component: RedactorLink,
      props: {
        setIsRedactorLinkOpen,
      },
    },
  ]);

  const handleKeyCommand = ({ command, e }, eState) => {
    if (command === 'tab') {
      const newEditorState = RichUtils.onTab(e, editorState, 4 /* maxDepth */);
      if (newEditorState !== editorState) {
        onChange(newEditorState);
        return 'handled';
      }
      return 'not-handled';
    }
    const newState = RichUtils.handleKeyCommand(eState, command);
    if (newState) {
      onChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const onHandleHyperLinkModal = () => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const startKey = selection.getStartKey();
    const startOffset = selection.getStartOffset();
    const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
    const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
    const linkInstance = linkKey ? contentState.getEntity(linkKey) : null;
    const url = linkInstance ? linkInstance.getData().url : '';
    setLinkUrl(url);
    setIsRedactorLinkOpen(true);
  };

  const onSaveHyperLink = (url) => {
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      // Normalize URL - prepend https:// if it starts with www.
      let normalizedUrl = url;
      if (url.startsWith('www.')) {
        normalizedUrl = `https://${url}`;
      }
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity(
        'LINK',
        'MUTABLE',
        { url: normalizedUrl, target: '_blank' },
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithEntity,
      });
      onChange(
        RichUtils.toggleLink(
          newEditorState,
          newEditorState.getSelection(),
          entityKey,
        ),
      );
    }
    setLinkUrl('');
    setIsRedactorLinkOpen(false);
  };

  const onRemoveHyperLink = () => {
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      const startKey = editorState.getSelection().getStartKey();
      const startOffset = editorState.getSelection().getStartOffset();
      const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
      const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
      if (linkKey) {
        onChange(RichUtils.toggleLink(editorState, selection, null));
      }
    }
    setValue('option.redactorUrl', 'https://');
    setIsRedactorLinkOpen(false);
  };

  const toggleBlockType = (blockType) => {
    if (blockType === 'link') {
      onHandleHyperLinkModal();
    } else {
      onChange(RichUtils.toggleBlockType(editorState, blockType));
    }
  };

  const toggleInlineStyle = (inlineStyle) => {
    onChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };

  const focusEditor = () => {
    editor.current.focus();
  };

  const finalEditorState = applyDecorators(editorState, compositeDecorators);

  return (
    <>
      {header && <Styled.InputHeader>{header}</Styled.InputHeader>}
      <Styled.EditorDiv
        className={`redactor ${className}`}
        border={border}
        onClick={focusEditor}
        fullScreen={fullScreen}
        newVersion={newVersion}
      >
        <Editor
          editorState={finalEditorState}
          onChange={onChange}
          handleKeyCommand={handleKeyCommand}
          keyBindingFn={myKeyBindingFn}
          placeholder={placeholder}
          spellCheck
          ref={editor}
          readOnly={disabled}
          data-cy={cy}
          onBlur={
            onBlur
              ? () => {
                  const currentContent = editorState.getCurrentContent();
                  const htmlContent = stateToHTML(currentContent);
                  onBlur(htmlContent);
                }
              : undefined
          }
        />
        <Styled.RedactorItemsDiv
          className="redactorItems"
          newVersion={newVersion}
        >
          <InlineStyleControls
            editorState={editorState}
            onToggle={toggleInlineStyle}
          />
          <BlockStyleControls
            editorState={editorState}
            onToggle={toggleBlockType}
          />
        </Styled.RedactorItemsDiv>
      </Styled.EditorDiv>
      <Tooltip
        anchorSelect=".redactor-link"
        place="top"
        clickable
        style={styledTooltip}
        render={({ content }) => (
          <Styled.TooltipContent>
            <Styled.LinkIcon color="#8F9BB3" size="16px" />
            {content}
            <Styled.Div>
              <Styled.EditIcon
                color="#8F9BB3"
                size="16px"
                onClick={onHandleHyperLinkModal}
              />
              <Styled.TrashIcon
                color="#E81E1E"
                size="16px"
                onClick={onRemoveHyperLink}
              />
            </Styled.Div>
          </Styled.TooltipContent>
        )}
      />
      <RedactorLinkModal
        initialLink={linkUrl}
        isOpen={isRedactorLinkOpen}
        close={() => {
          setIsRedactorLinkOpen(false);
          setLinkUrl('');
        }}
        onRemove={onRemoveHyperLink}
        onSave={onSaveHyperLink}
      />
    </>
  );
};

Redactor.defaultProps = {
  className: '',
  header: '',
  fullScreen: false,
  placeholder: '',
  cy: '',
  border: false,
  disabled: false,
  newVersion: false,
  onBlur: undefined,
};

Redactor.propTypes = {
  value: PropTypes.object.isRequired,
  className: PropTypes.string,
  header: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  fullScreen: PropTypes.bool,
  placeholder: PropTypes.string,
  cy: PropTypes.string,
  border: PropTypes.bool,
  disabled: PropTypes.bool,
  newVersion: PropTypes.bool,
  onBlur: PropTypes.func,
};

export default Redactor;
